/**
 * Instant patch proof — the customer's artwork drawn as a finished patch, in the browser.
 *
 * Everything happens on canvases in the visitor's tab: nothing is uploaded. The artwork is
 * (optionally) cut out of its background with the same colour-key approach as the background
 * remover, fitted inside a patch shape, given a twill ground and a merrowed or hot-cut border,
 * and — for the embroidered look — posterised and overlaid with stitch texture and a soft
 * emboss so it reads as thread rather than print.
 *
 * "Order this patch" writes the artwork plus the chosen size and shape to the same IndexedDB
 * hand-off the background remover uses (db mcp-tools / store handoff / key cutout); the patch
 * builder on the product page picks it up and fills itself in.
 *
 * This is a visual preview, not the production proof — the page says so, and so should anyone
 * touching this file.
 */
(() => {
  const HANDOFF = { db: 'mcp-tools', store: 'handoff', key: 'cutout' };
  const WORK = 1000; // long edge of the artwork working canvas, px
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  /* ───────────────────────── artwork preparation ───────────────────────── */

  const distance = (r1, g1, b1, r2, g2, b2) => {
    const dr = r1 - r2, dg = g1 - g2, db = b1 - b2;
    return Math.sqrt(dr * dr * 0.3 + dg * dg * 0.59 + db * db * 0.11) * 1.3;
  };

  /** Median colour of the outer 2px ring — what the artwork is sitting on. */
  function borderColour(data, width, height) {
    const rs = [], gs = [], bs = [];
    const take = (x, y) => { const i = (y * width + x) * 4; if (data[i + 3] > 200) { rs.push(data[i]); gs.push(data[i + 1]); bs.push(data[i + 2]); } };
    for (let x = 0; x < width; x += 1) { take(x, 0); take(x, 1); take(x, height - 1); take(x, height - 2); }
    for (let y = 0; y < height; y += 1) { take(0, y); take(1, y); take(width - 1, y); take(width - 2, y); }
    const mid = (list) => (list.length ? list.sort((a, b) => a - b)[list.length >> 1] : 255);
    return [mid(rs), mid(gs), mid(bs)];
  }

  /** Flood from the edges, fading out pixels close to the border colour, and un-mix the rim. */
  function cutOut(imageData, tolerance) {
    const { data, width, height } = imageData;
    const [br, bg, bb] = borderColour(data, width, height);
    const near = tolerance, far = tolerance * 1.5 + 14;
    const alphaOf = (i) => clamp((distance(data[i], data[i + 1], data[i + 2], br, bg, bb) - near) / (far - near), 0, 1);
    const alpha = new Float32Array(width * height).fill(1);
    const seen = new Uint8Array(width * height);
    const queue = [];
    const push = (p) => { if (!seen[p]) { seen[p] = 1; queue.push(p); } };
    for (let x = 0; x < width; x += 1) { push(x); push((height - 1) * width + x); }
    for (let y = 0; y < height; y += 1) { push(y * width); push(y * width + width - 1); }
    while (queue.length) {
      const p = queue.pop();
      const a = data[p * 4 + 3] < 8 ? 0 : alphaOf(p * 4);
      alpha[p] = a;
      if (a >= 1) continue;
      const x = p % width, y = (p / width) | 0;
      if (x > 0) push(p - 1); if (x < width - 1) push(p + 1);
      if (y > 0) push(p - width); if (y < height - 1) push(p + width);
    }
    for (let p = 0; p < alpha.length; p += 1) {
      const a = alpha[p], i = p * 4;
      if (a >= 1) continue;
      if (a <= 0) { data[i + 3] = 0; continue; }
      // The rim is a mix of artwork and background: take the background back out.
      data[i] = clamp((data[i] - br * (1 - a)) / a, 0, 255);
      data[i + 1] = clamp((data[i + 1] - bg * (1 - a)) / a, 0, 255);
      data[i + 2] = clamp((data[i + 2] - bb * (1 - a)) / a, 0, 255);
      data[i + 3] = Math.round(data[i + 3] * a);
    }
    return imageData;
  }

  /** Crop a canvas to its non-transparent pixels. */
  function trimmed(canvas) {
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const { data } = ctx.getImageData(0, 0, width, height);
    let top = height, left = width, right = -1, bottom = -1;
    for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > 10) { if (x < left) left = x; if (x > right) right = x; if (y < top) top = y; if (y > bottom) bottom = y; }
    }
    if (right < 0) return canvas;
    const out = make(right - left + 1, bottom - top + 1);
    out.getContext('2d').drawImage(canvas, left, top, out.width, out.height, 0, 0, out.width, out.height);
    return out;
  }

  const make = (w, h) => { const c = document.createElement('canvas'); c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h)); return c; };

  /** Thread colours have no gradients: snap each channel to a few levels. */
  function posterise(canvas, levels) {
    const ctx = canvas.getContext('2d');
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const step = 255 / (levels - 1);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 8) continue;
      d[i] = Math.round(d[i] / step) * step; d[i + 1] = Math.round(d[i + 1] / step) * step; d[i + 2] = Math.round(d[i + 2] / step) * step;
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
  }

  /** The most common non-white, non-transparent colour, for an automatic border. */
  function dominant(canvas) {
    const { data } = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    const buckets = new Map();
    for (let i = 0; i < data.length; i += 16) {
      if (data[i + 3] < 128) continue;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      if (r > 235 && g > 235 && b > 235) continue;
      const key = ((r >> 5) << 6) | ((g >> 5) << 3) | (b >> 5);
      const e = buckets.get(key) || { n: 0, r: 0, g: 0, b: 0 };
      e.n += 1; e.r += r; e.g += g; e.b += b; buckets.set(key, e);
    }
    let best = null;
    buckets.forEach((e) => { if (!best || e.n > best.n) best = e; });
    if (!best) return '#1A1B69';
    const hex = (v) => Math.round(v / best.n).toString(16).padStart(2, '0');
    return '#' + hex(best.r) + hex(best.g) + hex(best.b);
  }

  /* ───────────────────────────── masks & textures ─────────────────────────── */

  /** Grow a silhouette by r px: stamp it around a circle and fill the middle. */
  function dilate(mask, r) {
    const out = make(mask.width, mask.height);
    const ctx = out.getContext('2d');
    const steps = 32;
    for (let k = 0; k < steps; k += 1) {
      const a = (k / steps) * Math.PI * 2;
      ctx.drawImage(mask, Math.cos(a) * r, Math.sin(a) * r);
    }
    for (let rr = r * 0.66; rr > 0; rr -= Math.max(2, r / 4)) {
      for (let k = 0; k < 12; k += 1) { const a = (k / 12) * Math.PI * 2; ctx.drawImage(mask, Math.cos(a) * rr, Math.sin(a) * rr); }
    }
    ctx.drawImage(mask, 0, 0);
    return soften(out, 1.2);
  }

  function soften(canvas, px) {
    const out = make(canvas.width, canvas.height);
    const ctx = out.getContext('2d');
    ctx.filter = `blur(${px}px)`;
    ctx.drawImage(canvas, 0, 0);
    ctx.filter = 'none';
    return out;
  }

  /** Solid silhouette (any colour) from an artwork canvas's alpha. */
  function silhouette(canvas, colour) {
    const out = make(canvas.width, canvas.height);
    const ctx = out.getContext('2d');
    ctx.drawImage(canvas, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = colour;
    ctx.fillRect(0, 0, out.width, out.height);
    return out;
  }

  /** Path for the preset shapes, inset by `inset` px inside a w×h box at (x, y). */
  function shapePath(ctx, shape, x, y, w, h, inset) {
    const ix = x + inset, iy = y + inset, iw = w - inset * 2, ih = h - inset * 2;
    ctx.beginPath();
    if (shape === 'circle' || shape === 'oval') ctx.ellipse(ix + iw / 2, iy + ih / 2, iw / 2, ih / 2, 0, 0, Math.PI * 2);
    else if (shape === 'square' || shape === 'rect') { const r = Math.min(iw, ih) * 0.12; ctx.roundRect(ix, iy, iw, ih, r); }
    else if (shape === 'shield') {
      ctx.moveTo(ix + iw * 0.5, iy); ctx.lineTo(ix + iw, iy + ih * 0.18); ctx.lineTo(ix + iw * 0.92, iy + ih * 0.62);
      ctx.quadraticCurveTo(ix + iw * 0.78, iy + ih * 0.92, ix + iw * 0.5, iy + ih); ctx.quadraticCurveTo(ix + iw * 0.22, iy + ih * 0.92, ix + iw * 0.08, iy + ih * 0.62);
      ctx.lineTo(ix, iy + ih * 0.18); ctx.closePath();
    }
    ctx.closePath();
  }

  function pattern(ctx, draw, size, angle) {
    const tile = make(size, size);
    draw(tile.getContext('2d'), size);
    const p = ctx.createPattern(tile, 'repeat');
    if (p && angle && typeof DOMMatrix === 'function') p.setTransform(new DOMMatrix().rotate(angle));
    return p;
  }

  const shade = (hex, amount) => {
    const n = parseInt(hex.replace('#', ''), 16);
    const ch = (v) => clamp(Math.round(v + amount), 0, 255);
    return `rgb(${ch(n >> 16)}, ${ch((n >> 8) & 255)}, ${ch(n & 255)})`;
  };

  /* ───────────────────────────────── render ───────────────────────────────── */

  function render(state, canvas) {
    const { art, shape, style, border, borderColour: bc, twill, widthIn, heightIn, backdrop } = state;
    const ppi = 860 / Math.max(widthIn, heightIn);
    const pw = Math.round(widthIn * ppi), ph = Math.round(heightIn * ppi);
    const margin = 110;
    canvas.width = pw + margin * 2; canvas.height = ph + margin * 2;
    const ctx = canvas.getContext('2d');
    const borderW = Math.max(9, (border === 'merrowed' ? 0.115 : 0.05) * ppi);
    const px = margin, py = margin;

    // 1. Backdrop: a quiet fabric so white twill still reads.
    ctx.fillStyle = backdrop === 'dark' ? '#23253D' : '#EEF0F6';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const weave = pattern(ctx, (c, s) => { c.strokeStyle = backdrop === 'dark' ? 'rgba(255,255,255,.05)' : 'rgba(20,21,63,.05)'; c.lineWidth = 1; c.beginPath(); c.moveTo(0, s); c.lineTo(s, 0); c.stroke(); }, 6, 0);
    ctx.fillStyle = weave; ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 2. Inner (twill) and outer (border) masks, as canvases the size of the patch box.
    const inner = make(pw, ph), outer = make(pw, ph);
    const inset = borderW; // twill starts inside the border
    let artBox; // where the artwork goes
    if (shape === 'contour') {
      // Fit the art into the box leaving room for the border, then grow its silhouette.
      const pad = borderW * 1.9;
      const s = Math.min((pw - pad * 2) / art.width, (ph - pad * 2) / art.height);
      const aw = art.width * s, ah = art.height * s;
      artBox = { x: (pw - aw) / 2, y: (ph - ah) / 2, w: aw, h: ah };
      const sil = make(pw, ph); sil.getContext('2d').drawImage(silhouette(art, '#000'), artBox.x, artBox.y, aw, ah);
      const grown = dilate(sil, borderW * 0.55);
      inner.getContext('2d').drawImage(grown, 0, 0);
      outer.getContext('2d').drawImage(dilate(grown, borderW), 0, 0);
    } else {
      const ic = inner.getContext('2d'); shapePath(ic, shape, 0, 0, pw, ph, inset); ic.fillStyle = '#000'; ic.fill();
      const oc = outer.getContext('2d'); shapePath(oc, shape, 0, 0, pw, ph, 0); oc.fillStyle = '#000'; oc.fill();
      const pad = borderW * 1.6;
      const s = Math.min((pw - pad * 2) / art.width, (ph - pad * 2) / art.height) * (shape === 'circle' || shape === 'oval' ? 0.82 : 1);
      const aw = art.width * s, ah = art.height * s;
      artBox = { x: (pw - aw) / 2, y: (ph - ah) / 2, w: aw, h: ah };
    }

    // 3. Shadow under the whole patch.
    ctx.save(); ctx.shadowColor = 'rgba(0,0,0,.35)'; ctx.shadowBlur = 26; ctx.shadowOffsetY = 12;
    ctx.drawImage(silhouette(outer, backdrop === 'dark' ? '#000' : '#1A1B3A'), px, py); ctx.restore();

    // 4. Border: a filled ring of thread.
    const ring = make(pw, ph); const rc = ring.getContext('2d');
    rc.drawImage(outer, 0, 0); rc.globalCompositeOperation = 'source-in';
    rc.fillStyle = bc; rc.fillRect(0, 0, pw, ph);
    rc.globalCompositeOperation = 'source-atop';
    const threads = pattern(rc, (c, s) => { c.fillStyle = 'rgba(255,255,255,.22)'; c.fillRect(0, 0, s, 2); c.fillStyle = 'rgba(0,0,0,.22)'; c.fillRect(0, s - 2, s, 2); }, border === 'merrowed' ? 7 : 4, border === 'merrowed' ? 52 : 0);
    rc.fillStyle = threads; rc.fillRect(0, 0, pw, ph);
    // Rounded cord: lighter top-left, darker bottom-right.
    const sheen = rc.createLinearGradient(0, 0, pw, ph); sheen.addColorStop(0, 'rgba(255,255,255,.18)'); sheen.addColorStop(1, 'rgba(0,0,0,.22)');
    rc.fillStyle = sheen; rc.fillRect(0, 0, pw, ph);
    ctx.drawImage(ring, px, py);

    // 5. Twill ground inside the border.
    const ground = make(pw, ph); const gc = ground.getContext('2d');
    gc.drawImage(inner, 0, 0); gc.globalCompositeOperation = 'source-in'; gc.fillStyle = twill; gc.fillRect(0, 0, pw, ph);
    gc.globalCompositeOperation = 'source-atop';
    const tw = pattern(gc, (c, s) => { c.strokeStyle = 'rgba(0,0,0,.09)'; c.lineWidth = 1; c.beginPath(); c.moveTo(0, s); c.lineTo(s, 0); c.stroke(); c.strokeStyle = 'rgba(255,255,255,.12)'; c.beginPath(); c.moveTo(0, s - 1); c.lineTo(s - 1, 0); c.stroke(); }, 5, 0);
    gc.fillStyle = tw; gc.fillRect(0, 0, pw, ph);
    // Inner edge of the border throws a soft shadow onto the twill.
    gc.save(); gc.globalCompositeOperation = 'source-atop'; gc.shadowColor = 'rgba(0,0,0,.45)'; gc.shadowBlur = borderW * 0.9;
    gc.drawImage(silhouette(outer, '#000'), -pw * 2, 0); gc.restore(); // cheap: a shadow cast from an offscreen copy
    const vignette = gc.createRadialGradient(pw / 2, ph / 2, Math.min(pw, ph) * 0.25, pw / 2, ph / 2, Math.max(pw, ph) * 0.7);
    vignette.addColorStop(0, 'rgba(0,0,0,0)'); vignette.addColorStop(1, 'rgba(0,0,0,.14)'); gc.fillStyle = vignette; gc.fillRect(0, 0, pw, ph);
    ctx.drawImage(ground, px, py);
    if (style === 'leather' || style === 'pvc') {
      // Leather gets a grain, PVC a smooth gloss — both only inside the edge.
      const skin = make(pw, ph); const sc = skin.getContext('2d');
      sc.drawImage(inner, 0, 0); sc.globalCompositeOperation = 'source-in';
      if (style === 'leather') {
        sc.fillStyle = pattern(sc, (c, s) => { for (let i = 0; i < 22; i++) { c.fillStyle = i % 2 ? 'rgba(0,0,0,.11)' : 'rgba(255,255,255,.09)'; c.fillRect(Math.random() * s, Math.random() * s, 1.5, 1.5); } }, 28, 0);
      } else {
        const gloss = sc.createLinearGradient(0, 0, pw * 0.6, ph);
        gloss.addColorStop(0, 'rgba(255,255,255,.24)'); gloss.addColorStop(0.55, 'rgba(255,255,255,0)'); gloss.addColorStop(1, 'rgba(0,0,0,.10)');
        sc.fillStyle = gloss;
      }
      sc.fillRect(0, 0, pw, ph);
      ctx.drawImage(skin, px, py);
    }

    // 6. The artwork itself, made to look like thread or print.
    const layer = make(pw, ph); const lc = layer.getContext('2d');
    const prepared = style === 'embroidered' ? posterise(copy(art), 6) : style === 'pvc' ? posterise(copy(art), 8) : copy(art);
    lc.imageSmoothingQuality = 'high';
    lc.drawImage(prepared, artBox.x, artBox.y, artBox.w, artBox.h);
    if (style === 'embroidered') {
      lc.globalCompositeOperation = 'source-atop';
      const stitches = pattern(lc, (c, s) => { c.fillStyle = 'rgba(255,255,255,.17)'; c.fillRect(0, 0, s, 1); c.fillStyle = 'rgba(0,0,0,.17)'; c.fillRect(0, 2, s, 1); }, 4, 0);
      lc.fillStyle = stitches; lc.fillRect(0, 0, pw, ph);
      // Emboss: a hair of light top-left, shadow bottom-right, only over the thread.
      const sil = silhouette(prepared, '#fff'); const dark = silhouette(prepared, '#000');
      lc.globalAlpha = 0.28; lc.drawImage(sil, artBox.x - 1.5, artBox.y - 1.5, artBox.w, artBox.h);
      lc.drawImage(dark, artBox.x + 1.5, artBox.y + 1.5, artBox.w, artBox.h); lc.globalAlpha = 1;
    } else if (style === 'pvc') {
      // Moulded rubber: flat colour cells with a bevel and a sheen.
      lc.globalCompositeOperation = 'source-atop';
      lc.globalAlpha = 0.38;
      lc.drawImage(silhouette(prepared, '#fff'), artBox.x - 2, artBox.y - 2, artBox.w, artBox.h);
      lc.drawImage(silhouette(prepared, '#000'), artBox.x + 2, artBox.y + 2, artBox.w, artBox.h);
      lc.globalAlpha = 1;
      const sheenA = lc.createLinearGradient(0, 0, 0, ph); sheenA.addColorStop(0, 'rgba(255,255,255,.30)'); sheenA.addColorStop(0.5, 'rgba(255,255,255,0)');
      lc.fillStyle = sheenA; lc.fillRect(0, 0, pw, ph);
    } else if (style === 'leather') {
      // Laser-engraved: the artwork burns into the hide — dark where the art is dark, gone where it is
      // light. The brown tint here plus a multiply blend at compose time does the burning.
      lc.globalCompositeOperation = 'source-atop';
      lc.fillStyle = 'rgba(58, 34, 18, .62)'; lc.fillRect(0, 0, pw, ph);
    } else {
      lc.globalCompositeOperation = 'source-atop';
      lc.fillStyle = tw; lc.globalAlpha = 0.55; lc.fillRect(0, 0, pw, ph); lc.globalAlpha = 1;
    }
    // Keep the artwork inside the twill.
    lc.globalCompositeOperation = 'destination-in'; lc.drawImage(inner, 0, 0);
    if (style === 'leather') ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(layer, px, py);
    ctx.globalCompositeOperation = 'source-over';

    // 7. Size label.
    ctx.font = '600 22px Inter, system-ui, sans-serif'; ctx.fillStyle = backdrop === 'dark' ? 'rgba(255,255,255,.7)' : 'rgba(20,21,63,.6)'; ctx.textAlign = 'center';
    ctx.fillText(`${trimNum(widthIn)}" × ${trimNum(heightIn)}"  ·  ${({ embroidered: 'Embroidered', printed: 'Full colour printed', pvc: 'PVC rubber', leather: 'Leather' })[style] || 'Printed'}  ·  ${border === 'merrowed' ? 'Merrowed border' : 'Hot-cut border'}`, canvas.width / 2, canvas.height - 34);
  }

  const copy = (c) => { const o = make(c.width, c.height); o.getContext('2d').drawImage(c, 0, 0); return o; };
  const trimNum = (n) => String(Math.round(n * 2) / 2).replace(/\.0$/, '');

  /** Artwork from an <img>: scaled to the working size and, by default, cut out of its background. */
  function prepareImage(source, { cutout = true, tolerance = 60 } = {}) {
    const s = WORK / Math.max(source.naturalWidth, source.naturalHeight);
    const c = make(source.naturalWidth * s, source.naturalHeight * s);
    const cx = c.getContext('2d'); cx.imageSmoothingQuality = 'high';
    cx.drawImage(source, 0, 0, c.width, c.height);
    if (!cutout) return c;
    const img = cx.getImageData(0, 0, c.width, c.height); cutOut(img, tolerance); cx.putImageData(img, 0, 0);
    return trimmed(c);
  }

  /* ─────────────────────────────── hand-off ─────────────────────────────── */

  function remember(record) {
    return new Promise((resolve, reject) => {
      const open = indexedDB.open(HANDOFF.db, 1);
      open.onupgradeneeded = () => open.result.createObjectStore(HANDOFF.store);
      open.onerror = () => reject(open.error);
      open.onsuccess = () => {
        const db = open.result;
        const tx = db.transaction(HANDOFF.store, 'readwrite');
        tx.objectStore(HANDOFF.store).put({ ...record, at: Date.now() }, HANDOFF.key);
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
      };
    });
  }

  /* ─────────────────────────────── the page ─────────────────────────────── */

  function init(root) {
    const q = (s) => root.querySelector(s);
    const drop = q('[data-pp-drop]'), fileInput = q('[data-pp-file]'), stage = q('[data-pp-stage]'), canvas = q('[data-pp-canvas]');
    const status = q('[data-pp-status]'), controls = q('[data-pp-controls]');
    const W = q('[data-pp-w]'), H = q('[data-pp-h]'), lock = q('[data-pp-lock]');
    const cutToggle = q('[data-pp-cutout]'), tol = q('[data-pp-tolerance]');
    const borderColourInput = q('[data-pp-border-colour]'), autoBorder = q('[data-pp-border-auto]');
    const twillInput = q('[data-pp-twill]');
    const download = q('[data-pp-download]'), order = q('[data-pp-order]'), reset = q('[data-pp-reset]');
    const links = { embroidered: root.dataset.embroideredUrl || '/products/embroidered-patches', printed: root.dataset.printedUrl || '/products/full-color-printed-patches' };

    let source = null, name = 'artwork', originalBlob = null, ratio = 1, busy = false;
    const state = { art: null, shape: 'contour', style: 'embroidered', border: 'merrowed', borderColour: '#1A1B69', twill: '#FFFFFF', widthIn: 3, heightIn: 3, backdrop: 'light' };
    const say = (m, bad) => { if (!status) return; status.textContent = m || ''; status.hidden = !m; status.classList.toggle('is-error', !!bad); };

    const pick = (sel) => root.querySelector(`[data-pp-pick="${sel}"] [aria-pressed="true"]`);
    root.querySelectorAll('[data-pp-pick]').forEach((group) => group.addEventListener('click', (e) => {
      const b = e.target.closest('button[data-value]'); if (!b) return;
      group.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      state[group.dataset.ppPick] = b.dataset.value;
      if (group.dataset.ppPick === 'shape') syncSizeForShape();
      schedule();
    }));

    function prepare() {
      if (!source) return;
      const out = prepareImage(source, { cutout: !!(cutToggle && cutToggle.checked), tolerance: Number(tol && tol.value) || 60 });
      state.art = out;
      ratio = out.height / out.width;
      if (autoBorder && autoBorder.checked) { state.borderColour = dominant(out); if (borderColourInput) borderColourInput.value = state.borderColour; }
      syncSizeForShape();
    }

    function syncSizeForShape() {
      const w = clamp(parseFloat(W.value) || 3, 1, 15);
      let h;
      if (state.shape === 'circle' || state.shape === 'square') h = w;
      else if (lock && lock.checked) h = clamp(Math.round(w * ratio * 2) / 2, 1, 15);
      else h = clamp(parseFloat(H.value) || w, 1, 15);
      W.value = trimNum(w); H.value = trimNum(h);
      state.widthIn = w; state.heightIn = h;
      H.disabled = state.shape === 'circle' || state.shape === 'square';
    }

    let timer = null;
    function schedule() { clearTimeout(timer); timer = setTimeout(draw, 40); }
    function draw() {
      if (!state.art || busy) return;
      busy = true;
      try { render(state, canvas); stage.hidden = false; controls.hidden = false; say(''); }
      catch (e) { say('That image could not be drawn. Try a PNG or JPG under 20 MB.', true); }
      busy = false;
    }

    function load(file) {
      if (!file || !/^image\//.test(file.type)) { say('Please choose a PNG, JPG, WEBP or SVG.', true); return; }
      name = (file.name || 'artwork').replace(/\.[^.]+$/, '') || 'artwork';
      originalBlob = file;
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); source = img; prepare(); draw(); root.classList.add('has-art'); };
      img.onerror = () => { URL.revokeObjectURL(url); say('That file could not be read as an image.', true); };
      img.src = url;
    }

    if (fileInput) fileInput.addEventListener('change', () => load(fileInput.files && fileInput.files[0]));
    if (drop) {
      ['dragenter', 'dragover'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
      ['dragleave', 'drop'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('is-over'); }));
      drop.addEventListener('drop', (e) => load(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]));
    }
    document.addEventListener('paste', (e) => { const item = [...(e.clipboardData && e.clipboardData.items || [])].find((i) => /^image\//.test(i.type)); if (item) load(item.getAsFile()); });

    [W, H].forEach((el) => el && el.addEventListener('input', () => { if (el === W) syncSizeForShape(); else { state.heightIn = clamp(parseFloat(H.value) || 3, 1, 15); if (lock && lock.checked) { lock.checked = false; } } schedule(); }));
    if (lock) lock.addEventListener('change', () => { syncSizeForShape(); schedule(); });
    if (cutToggle) cutToggle.addEventListener('change', () => { prepare(); schedule(); });
    if (tol) tol.addEventListener('input', () => { if (cutToggle && cutToggle.checked) { prepare(); schedule(); } });
    if (borderColourInput) borderColourInput.addEventListener('input', () => { state.borderColour = borderColourInput.value; if (autoBorder) autoBorder.checked = false; schedule(); });
    if (autoBorder) autoBorder.addEventListener('change', () => { if (autoBorder.checked && state.art) { state.borderColour = dominant(state.art); borderColourInput.value = state.borderColour; } schedule(); });
    if (twillInput) twillInput.addEventListener('input', () => { state.twill = twillInput.value; schedule(); });

    if (download) download.addEventListener('click', () => {
      canvas.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${name}-patch-proof.png`; a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      }, 'image/png');
    });

    if (order) order.addEventListener('click', async () => {
      const target = links[state.style] || links.embroidered;
      const shapeName = { contour: 'Custom Shape', circle: 'Circle', square: 'Square', rect: 'Rectangle', oval: 'Oval', shield: 'Custom Shape' }[state.shape];
      try {
        // Hand over the cut-out if one was made, otherwise the original file — never the proof itself.
        const blob = cutToggle && cutToggle.checked && state.art ? await new Promise((r) => state.art.toBlob(r, 'image/png')) : originalBlob;
        await remember({ blob, name: cutToggle && cutToggle.checked ? `${name}-cutout.png` : (originalBlob && originalBlob.name) || name, size: { w: state.widthIn, h: state.heightIn }, shape: shapeName, from: 'patch-proof' });
      } catch (e) { /* the product page still works without the hand-off */ }
      location.href = target;
    });

    if (reset) reset.addEventListener('click', () => { source = null; state.art = null; stage.hidden = true; controls.hidden = true; root.classList.remove('has-art'); if (fileInput) fileInput.value = ''; say(''); });

    root.setAttribute('data-pp-ready', '');
  }

  // The patch builder draws the same proof inside its artwork step.
  window.MCPProof = { prepareImage, render, dominant };

  const boot = () => document.querySelectorAll('[data-patch-proof]:not([data-pp-ready])').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
